import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FiShield,
    FiUsers,
    FiInbox,
    FiUserCheck,
    FiCheckCircle,
    FiAlertCircle,
    FiClock,
    FiArrowUpRight,
} from "react-icons/fi";
import {
    PageHeader,
    MetricCard,
    StatusBadge,
    EmptyState,
    SkeletonMetricCard,
} from "../../theme/components";
import { PageButton } from "../../theme/components/PageButton";
import { adminApi } from "../../api/endpoints/admin";
import { contactApi } from "../../api/endpoints/contact";
import { formatDateTimeDisplay } from "../../utils/dates";
import "./AdminDashboard.css";

const STATUS_SEVERITY = {
    new: "info",
    read: "warning",
    resolved: "success",
};

const formatDate = (iso) => formatDateTimeDisplay(iso);

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const [s, list] = await Promise.all([
                    adminApi.getStats(),
                    contactApi.list(),
                ]);
                if (cancelled) return;
                setStats(s);
                setRecent(list.slice(0, 5));
            } catch (e) {
                if (cancelled) return;
                setError(
                    e?.response?.data?.message || e?.message || "Failed to load admin data"
                );
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    if (error) {
        return (
            <div className="admin-wrapper">
                <PageHeader
                    title="Admin Dashboard"
                    subtitle="System overview and help messages"
                    icon={FiShield}
                />
                <div className="admin-body">
                    <EmptyState
                        illustration="error"
                        title="Couldn't load admin data"
                        description={error}
                        action={
                            <PageButton
                                type="button"
                                variant="primary"
                                onClick={() => window.location.reload()}
                            >
                                Try again
                            </PageButton>
                        }
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-wrapper">
            <PageHeader
                title="Admin Dashboard"
                subtitle="System overview, user activity, and help messages"
                icon={FiShield}
            />

            <div className="admin-body">
                <div className="admin-metrics-grid">
                    {!stats ? (
                        <>
                            <SkeletonMetricCard />
                            <SkeletonMetricCard />
                            <SkeletonMetricCard />
                            <SkeletonMetricCard />
                            <SkeletonMetricCard />
                        </>
                    ) : (
                        <>
                            <MetricCard
                                label="Active Children"
                                value={stats.activeChildren}
                                accent="primary"
                                icon={FiUsers}
                                helperText={`of ${stats.totalChildren} total`}
                                onClick={() => navigate("/profiles")}
                            />
                            <MetricCard
                                label="Active Users"
                                value={stats.activeUsers}
                                accent="secondary"
                                icon={FiUserCheck}
                                helperText={`of ${stats.totalUsers} total`}
                                onClick={() => navigate("/admin/users")}
                            />
                            <MetricCard
                                label="New Messages"
                                value={stats.newMessages}
                                accent="info"
                                icon={FiInbox}
                                trend={
                                    stats.openMessages > 0
                                        ? { direction: "up", value: `${stats.openMessages} open` }
                                        : undefined
                                }
                                onClick={() => navigate("/admin/messages")}
                            />
                            <MetricCard
                                label="Resolved"
                                value={stats.resolvedMessages}
                                accent="success"
                                icon={FiCheckCircle}
                                helperText={`of ${stats.totalMessages} total`}
                                onClick={() =>
                                    navigate("/admin/messages?status=resolved")
                                }
                            />
                            <MetricCard
                                label="Open (new + read)"
                                value={stats.openMessages}
                                accent="warning"
                                icon={FiClock}
                                onClick={() => navigate("/admin/messages")}
                            />
                        </>
                    )}
                </div>

                <div className="admin-recent-card">
                    <div className="admin-recent-header">
                        <div>
                            <h2 className="admin-recent-title">Recent Help Messages</h2>
                            <span className="admin-recent-sub">
                                The 5 most recent user submissions.
                            </span>
                        </div>
                        <PageButton
                            type="button"
                            variant="ghost"
                            endIcon={<FiArrowUpRight size={16} />}
                            onClick={() => navigate("/admin/messages")}
                        >
                            View all
                        </PageButton>
                    </div>

                    {recent === null ? (
                        <div className="admin-recent-skeleton">
                            {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="admin-recent-skel-row" />
                            ))}
                        </div>
                    ) : recent.length === 0 ? (
                        <EmptyState
                            illustration="no-data"
                            title="No help messages yet"
                            description="When users submit feedback via the Help page, it'll show up here."
                            compact
                        />
                    ) : (
                        <ul className="admin-recent-list">
                            {recent.map((m) => (
                                <li
                                    key={m.id}
                                    className="admin-recent-row"
                                    onClick={() => navigate(`/admin/messages/${m.id}`)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            navigate(`/admin/messages/${m.id}`);
                                        }
                                    }}
                                >
                                    <div className="admin-recent-row-main">
                                        <div className="admin-recent-row-top">
                                            <span className="admin-recent-email">
                                                {m.userEmail}
                                            </span>
                                            <StatusBadge
                                                severity={STATUS_SEVERITY[m.status] || "neutral"}
                                                size="small"
                                            >
                                                {m.status}
                                            </StatusBadge>
                                        </div>
                                        <div className="admin-recent-row-msg">
                                            {m.message}
                                        </div>
                                        <div className="admin-recent-row-meta">
                                            <span className="admin-recent-topic">
                                                {m.topic}
                                                {m.customReason ? ` · ${m.customReason}` : ""}
                                            </span>
                                            <span>{formatDate(m.createdAt)}</span>
                                        </div>
                                    </div>
                                    <FiAlertCircle
                                        size={16}
                                        className="admin-recent-row-icon"
                                        aria-hidden="true"
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
